import React, { useState, useEffect } from 'react';
import useFetch from 'use-http';
import { BiX } from 'react-icons/bi';

// --- Types ---
interface IProps {
    isOpen: boolean;
    onClose: () => void;
    liTg: number | null;
    onSuccess: () => void;
}

interface IPaidByOther {
    liTg: number;
    paidDate: string;
    paidFlag: string;
    notes: string;
}

// --- Constants ---
const PAID_TYPE_OPTIONS = [
    { value: 'O', label: 'Paid By Other' },
    { value: 'Y', label: 'Paid By Other - Unknown' },
    { value: 'Z', label: 'Zero Bonus' }
];

const PaidByOtherModal: React.FC<IProps> = ({ isOpen, onClose, liTg, onSuccess }) => {
    // Added 'del' to the destructured hook
    const { get, post, del, response, loading } = useFetch();
    
    const [formData, setFormData] = useState<IPaidByOther>({
        liTg: 0,
        paidDate: new Date().toISOString().split('T')[0],
        paidFlag: '',
        notes: ''
    });

    // Track if we are editing an existing record to show/hide the Delete button
    const [isEditMode, setIsEditMode] = useState(false);

    // --- Load Data ---
    useEffect(() => {
        if (isOpen && liTg) {
            const loadData = async () => {
                const data = await get(`/v1.0/leases/invoices/${liTg}/paid-other`);
                if (response.ok && data) {
                    setFormData({
                        liTg: liTg,
                        paidDate: data.paidDate ? data.paidDate.split('T')[0] : new Date().toISOString().split('T')[0],
                        paidFlag: data.paidFlag || '',
                        notes: data.notes || ''
                    });
                    setIsEditMode(true); // Record exists
                } else {
                    setFormData({
                        liTg: liTg,
                        paidDate: new Date().toISOString().split('T')[0],
                        paidFlag: 'O', // Default
                        notes: ''
                    });
                    setIsEditMode(false); // New record
                }
            };
            loadData();
        }
    }, [isOpen, liTg, get, response]);

    // --- Handlers ---
    const handleSave = async () => {
        if (!formData.paidFlag) {
            alert('Please select a Type.');
            return;
        }
        if (!formData.paidDate) {
            alert('Please enter a Paid Date.');
            return;
        }

        await post('/v1.0/leases/invoices/paid-other', formData);

        if (response.ok) {
            onSuccess();
            onClose();
        } else {
            alert('Failed to save record.');
        }
    };

    const handleDelete = async () => {
        if (!liTg) { return; }
        if (!confirm('Are you sure you want to remove this \'Paid By Other\' record?')) { return; }

        await del(`/v1.0/leases/invoices/${liTg}/paid-other`);

        if (response.ok) {
            onSuccess();
            onClose();
        } else {
            alert('Failed to remove record.');
        }
    };

    if (!isOpen) { return null; }

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                
                {/* Header */}
                <div style={styles.modalHeader}>
                    <h3 style={styles.modalTitle}>Lease Paid By Other</h3>
                    <button onClick={onClose} style={styles.iconButton}><BiX size={24} /></button>
                </div>

                {/* Body */}
                <div style={styles.modalBody}>
                    <div style={styles.formGrid}>
                        {/* Paid Date */}
                        <div style={styles.fieldContainer}>
                            <label style={styles.fieldLabel}>Paid Date</label>
                            <input 
                                type="date" 
                                style={styles.selectBase} 
                                value={formData.paidDate}
                                onChange={e => setFormData({...formData, paidDate: e.target.value})}
                            />
                        </div>

                        {/* Type Selection */}
                        <div style={styles.fieldContainer}>
                            <label style={styles.fieldLabel}>Type</label>
                            <select 
                                style={styles.selectBase}
                                value={formData.paidFlag}
                                onChange={e => setFormData({...formData, paidFlag: e.target.value})}
                            >
                                <option value="">Select Type...</option>
                                {PAID_TYPE_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Notes */}
                        <div style={styles.fieldContainer}>
                            <label style={styles.fieldLabel}>Notes</label>
                            <input 
                                type="text"
                                style={styles.selectBase}
                                value={formData.notes}
                                onChange={e => setFormData({...formData, notes: e.target.value})}
                                placeholder="Enter notes..."
                            />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div style={styles.modalFooter}>
                    <div style={{ marginRight: 'auto' }}>
                        {isEditMode && (
                            <button 
                                onClick={handleDelete} 
                                style={{...styles.buttonSecondary, color: '#DC2626', borderColor: '#FCA5A5', backgroundColor: '#FEF2F2'}}
                                disabled={loading}
                            >
                                Remove
                            </button>
                        )}
                    </div>
                    <div style={{ display: 'flex' }}>
                        <button onClick={onClose} style={styles.buttonSecondary}>Cancel</button>
                        <button onClick={handleSave} style={{...styles.buttonPrimary, marginLeft: '0.75rem'}} disabled={loading}>
                            {loading ? 'Saving...' : 'OK'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Styles ---
const styles: { [key: string]: React.CSSProperties } = {
    modalOverlay: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex',
        justifyContent: 'center', alignItems: 'center', zIndex: 1000,
        backdropFilter: 'blur(3px)',
    },
    modalContent: {
        backgroundColor: '#FFFFFF', padding: '1.5rem', borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
        width: '90%', maxWidth: '800px', 
        display: 'flex', flexDirection: 'column', gap: '1rem',
    },
    modalHeader: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        paddingBottom: '1rem', borderBottom: '1px solid #E5E7EB',
    },
    modalTitle: {
        fontSize: '1.25rem', fontWeight: 600, color: '#111827', margin: 0,
    },
    modalBody: {
        padding: '0.5rem 0',
    },
    formGrid: {
        display: 'grid',
        gridTemplateColumns: '160px 240px 1fr', 
        gap: '1.5rem',
        alignItems: 'end',
    },
    fieldContainer: {
        display: 'flex', flexDirection: 'column', gap: '0.35rem',
    },
    fieldLabel: {
        fontSize: '0.875rem', color: '#374151', fontWeight: 500,
    },
    selectBase: {
        padding: '0.5rem 0.75rem', border: '1px solid #D1D5DB',
        borderRadius: '6px', fontSize: '1rem', color: '#111827',
        backgroundColor: '#fff', outline: 'none', transition: 'all 0.2s',
        width: '100%', boxSizing: 'border-box',
    },
    modalFooter: {
        display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
        marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #E5E7EB',
    },
    buttonPrimary: {
        padding: '0.6rem 1.5rem', border: 'none',
        borderRadius: '6px', backgroundColor: '#2563EB', color: 'white',
        cursor: 'pointer', fontWeight: 500, fontSize: '1rem',
        display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '80px'
    },
    buttonSecondary: {
        padding: '0.6rem 1.5rem', border: '1px solid #D1D5DB',
        borderRadius: '6px', backgroundColor: '#FFFFFF', color: '#374151',
        cursor: 'pointer', fontWeight: 500, fontSize: '1rem',
    },
    iconButton: {
        background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280'
    }
};

export default PaidByOtherModal;


// WEBPACK FOOTER //
// ./src/components/invoicing/components/PaidByOtherModal.tsx